import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import API_BASE_URL from '../../../../Constants';
import DeviceAccordion from '../../../setup/components/Device List/components/DeviceAccordion';
import RenderFields from '../InspectionList/RenderFields';
import DeviceListTable from '../InspectionList/DeviceListTable';

/** 
 * The same field arrays as your second snippet:
 **/
const inspectionMainInfo = [
  { name: 'agencyName',    label: 'Agency',        type: 'text', readOnly: true },
  { name: 'inspectionId',  label: 'Inspection #',  type: 'text', readOnly: true },
  { name: 'taskType',      label: 'Task Type',     type: 'text' },
  { name: 'status',        label: 'Status',        type: 'text' },
  { name: 'nextDate',      label: 'Next Date',     type: 'date' },
  { name: 'approvalDate',  label: 'Approval Date', type: 'date' },
];

const inspectionOtherInfo = [
  { name: 'managementCompanyId', label: 'Management Company ID', type: 'text' },
  { name: 'customerName',        label: 'Customer Name',         type: 'text' },
  { name: 'testId',              label: 'Test ID (Agency Test)', type: 'text', readOnly: true },
  { name: 'buildingId',          label: 'Building ID',           type: 'text', readOnly: true },
  {
    name: 'categoryInspection',
    label: 'Category Inspection',
    type: 'text',
    readOnly: true,
  },
];

// Helper to split fields for two-column layout
function splitFields(fieldsArray) {
  const midIndex = Math.ceil(fieldsArray.length / 2);
  return [fieldsArray.slice(0, midIndex), fieldsArray.slice(midIndex)];
}

/**
 * Unified modal that:
 *  - Fetches the inspection, building, linked inspection, devices, service companies
 *  - Shows the same "Main Info" & "Other Info" fields
 *  - Includes a device table
 *  - Allows Next/Prev arrows at top
 *  - PUT (save) and DELETE for the inspection
 */
export default function ViewInspectionDeviceListModal({
  open,
  onClose,
  refreshList,
  setNext,
  setPrev,
  inspectionNumber,   // e.g. "2025-00012"
  selectedInspection, // includes agencyName, buildingId, etc.
}) {
  const [inspectionData, setInspectionData]   = useState(null);
  const [linkedInspection, setLinkedInspection] = useState(null);
  const [buildingData, setBuildingData]       = useState(null);
  const [devices, setDevices]                 = useState([]);
  const [maintenanceCompanies, setMaintenanceCompanies] = useState([]);
  const [performingCompanies, setPerformingCompanies]   = useState([]);
  const [witnessCompanies, setWitnessCompanies]         = useState([]);

  const [isEditMode, setIsEditMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // We rely on inspectionId from the selected row
  const inspectionId = selectedInspection?.inspectionId || null;

  // Split your two field arrays
  const [mainLeft, mainRight]   = splitFields(inspectionMainInfo);
  const [otherLeft, otherRight] = splitFields(inspectionOtherInfo);

  // --------------------------------------------------
  // 1) Fetch the main inspection
  // --------------------------------------------------
  useEffect(() => {
    if (!open || !inspectionId) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/inspections/id/${inspectionId}`, {
      credentials: 'include',
    })
      .then((resp) => {
        if (!resp.ok) {
          throw new Error(`Could not fetch inspection. Status: ${resp.status}`);
        }
        return resp.json();
      })
      .then((data) => setInspectionData(data))
      .catch((err) => {
        console.error('Error fetching inspection:', err);
        setInspectionData(null);
      })
      .finally(() => setLoading(false));
  }, [open, inspectionId]);

  // --------------------------------------------------
  // 2) After we have the inspection, fetch a "linked" inspection if present
  // --------------------------------------------------
  useEffect(() => {
    if (!inspectionData?.linkedInspectionId) {
      setLinkedInspection(null);
      return;
    }
    const linkedId = inspectionData.linkedInspectionId;
    fetch(`${API_BASE_URL}/api/inspections/id/${linkedId}`, {
      credentials: 'include',
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Could not fetch linked insp. Status: ${r.status}`);
        return r.json();
      })
      .then((ld) => setLinkedInspection(ld))
      .catch(() => setLinkedInspection(null));
  }, [inspectionData]);

  // --------------------------------------------------
  // 3) Building ID => fetch building + devices
  // --------------------------------------------------
  const buildingId =
    inspectionData?.buildingId || selectedInspection?.buildingId || null;

  // 3a) Building
  useEffect(() => {
    if (!open || !buildingId) return;
    fetch(`${API_BASE_URL}/api/setup/buildings/${buildingId}`, {
      credentials: 'include',
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Could not fetch building. ${r.status}`);
        return r.json();
      })
      .then((bd) => setBuildingData(bd))
      .catch(() => setBuildingData(null));
  }, [open, buildingId]);

  // 3b) Devices
  useEffect(() => {
    if (!open || !buildingId) return;
    fetch(`${API_BASE_URL}/api/setup/buildings/devices/${buildingId}`, {
      credentials: 'include',
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Could not fetch devices. ${r.status}`);
        return r.json();
      })
      .then((d) => setDevices(Array.isArray(d) ? d : []))
      .catch(() => setDevices([]));
  }, [open, buildingId]);

  // --------------------------------------------------
  // 4) Service-company endpoints
  // --------------------------------------------------
  useEffect(() => {
    if (!open) return;
    // 4a) Maintenance
    fetch(`${API_BASE_URL}/api/inspections/service-companies/maintenance`, {
      credentials: 'include',
    })
      .then((r) => r.json())
      .then((data) =>
        setMaintenanceCompanies(Array.isArray(data) ? data : [])
      )
      .catch(() => setMaintenanceCompanies([]));

    // 4b) Performing
    fetch(`${API_BASE_URL}/api/inspections/service-companies/performing`, {
      credentials: 'include',
    })
      .then((r) => r.json())
      .then((data) => setPerformingCompanies(Array.isArray(data) ? data : []))
      .catch(() => setPerformingCompanies([]));

    // 4c) Witness
    fetch(`${API_BASE_URL}/api/inspections/service-companies/witness`, {
      credentials: 'include',
    })
      .then((r) => r.json())
      .then((data) => setWitnessCompanies(Array.isArray(data) ? data : []))
      .catch(() => setWitnessCompanies([]));
  }, [open]);

  // --------------------------------------------------
  // 5) Close / expand
  // --------------------------------------------------
  const handleClose = () => {
    if (!isEditMode) {
      onClose();
    }
  };
  const toggleExpand = () => setIsExpanded(!isExpanded);

  // --------------------------------------------------
  // 6) Validation & Save
  // --------------------------------------------------
  function validateFields() {
    const errs = {};
    // custom logic if needed
    setValidationErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function anyDeviceHasViolations() {
    return devices.some((d) => d.violationCount && d.violationCount > 0);
  }

  async function handleSaveChanges() {
    if (!validateFields()) {
      alert('Please fix errors before saving.');
      return;
    }
    // Optional example: if any device has violations, force status=Active
    let finalStatus = inspectionData?.status || 'Inactive';
    if (anyDeviceHasViolations()) {
      finalStatus = 'Active';
    }
    const updatedObj = { ...inspectionData, status: finalStatus };

    try {
      const resp = await fetch(
        `${API_BASE_URL}/api/inspections/id/${inspectionId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(updatedObj),
        }
      );
      if (!resp.ok) {
        throw new Error(`Failed to update. Status: ${resp.status}`);
      }
      alert(`Saved successfully. Status => ${finalStatus}`);
      setIsEditMode(false);
      if (refreshList) refreshList();
    } catch (err) {
      alert('Failed to save changes.');
    }
  }

  async function handleDelete() {
    if (!window.confirm('Delete inspection?')) return;
    try {
      const resp = await fetch(
        `${API_BASE_URL}/api/inspections/id/${inspectionId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );
      if (!resp.ok) {
        throw new Error(`Delete failed: ${resp.status}`);
      }
      alert('Inspection deleted.');
      onClose();
      if (refreshList) refreshList();
    } catch {
      alert('Failed to delete.');
    }
  }

  function handleFieldChange(e) {
    const { name, value } = e.target;
    setInspectionData((prev) => ({ ...prev, [name]: value }));
  }

  // --------------------------------------------------
  // 7) Merge final data for display
  // --------------------------------------------------
  const mergedInsp = { ...selectedInspection, ...inspectionData };
  const finalAgencyName =
    buildingData?.agencyName || mergedInsp.agencyName || '';

  // The data we pass to <RenderFields/>
  const combinedData = {
    agencyName: finalAgencyName,
    inspectionId: mergedInsp.inspectionId || inspectionNumber || '',
    taskType: mergedInsp.taskType || '',
    status: mergedInsp.status || '',
    nextDate: mergedInsp.nextDate || '',
    approvalDate: mergedInsp.approvalDate || '',
    managementCompanyId: mergedInsp.managementCompanyId || '',
    customerName: mergedInsp.customerName || '',
    testId: mergedInsp.testId || '',
    buildingId: mergedInsp.buildingId || '',
    categoryInspection: mergedInsp.categoryInspection || mergedInsp.test || '',
  };

  const displayedInspectionNumber =
    mergedInsp.inspectionNumber ||
    mergedInsp.inspectionId ||
    inspectionNumber ||
    'N/A';

  // If there's a "linked inspection"
  const linkedInspId = mergedInsp.linkedInspectionId;

  // --------------------------------------------------
  // Render
  // --------------------------------------------------
  return (
    <>
      {/* Only show arrows if open, not editing, and we have next/prev handlers */}
      {open && !isEditMode && (
        <>
          {setPrev && (
            <button
              onClick={setPrev}
              style={{
                position: 'fixed',
                left: `calc(50% - ${isExpanded ? '45%' : '500px'} - 60px)`,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1500,
              }}
              className="p-2 rounded-full bg-white hover:bg-gray-200 transition"
            >
              <ArrowBackIcon />
            </button>
          )}
          {setNext && (
            <button
              onClick={setNext}
              style={{
                position: 'fixed',
                right: `calc(50% - ${isExpanded ? '45%' : '500px'} - 60px)`,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1500,
              }}
              className="p-2 rounded-full bg-white hover:bg-gray-200 transition"
            >
              <ArrowForwardIcon />
            </button>
          )}
        </>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          style: {
            width: isExpanded ? '90%' : '1000px',
            maxHeight: '72vh',
            minHeight: '72vh',
          },
        }}
        fullWidth
        style={{ zIndex: 1400 }}
      >
        <div className="sticky top-0 bg-white z-10">
          {/* Header row */}
          <div className="flex justify-between items-center p-4">
            <div className="flex items-center space-x-4 flex-grow">
              <button
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-gray-200 transition"
              >
                <ArrowBackIcon />
              </button>
              <h2 className="text-2xl font-bold text-left">
                {isEditMode
                  ? `Edit Inspection # ${displayedInspectionNumber}`
                  : `View Inspection # ${displayedInspectionNumber}`}
              </h2>
            </div>
            <button
              onClick={toggleExpand}
              className="p-2 rounded-full hover:bg-gray-200 transition"
            >
              {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </button>
          </div>

          {/* Action buttons */}
          <div className="flex space-x-4 p-4">
            {isEditMode ? (
              <>
                <button
                  onClick={() => setIsEditMode(false)}
                  className="px-4 py-2 bg-gray-300 text-black font-bold rounded-md hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  className="px-6 py-2 bg-yellow-500 text-white font-bold rounded-md hover:bg-yellow-600 transition"
                >
                  Save
                </button>
                <button
                  onClick={handleDelete}
                  className="px-6 py-2 bg-red-500 text-white font-bold rounded-md hover:bg-red-600 transition"
                >
                  Delete
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditMode(true)}
                className="px-6 py-2 bg-yellow-500 text-white font-bold rounded-md hover:bg-yellow-600 transition"
              >
                Edit
              </button>
            )}
          </div>
        </div>

        <DialogContent dividers>
          {loading ? (
            <p>Loading inspection data...</p>
          ) : (
            <>
              {/* If there's a linked inspection, show a summary + a button */}
              {linkedInspId && inspectionData && linkedInspection && (
                <div className="mb-4">
                  <div className="p-3 mb-2 border border-yellow-400 rounded-md bg-yellow-50 text-yellow-900">
                    <p>
                      <strong>Current Inspection:</strong> #
                      {inspectionData.inspectionNumber} (
                      {inspectionData.test || 'Unknown Category'})
                    </p>
                    <p className="text-sm">
                      Building: {inspectionData.buildingId}, Year:{' '}
                      {inspectionData.inspYear}
                    </p>
                  </div>
                  <div className="p-3 border border-blue-300 rounded-md bg-blue-50 text-blue-900">
                    <p>
                      <strong>Linked Inspection:</strong> #
                      {linkedInspection.inspectionNumber} (
                      {linkedInspection.test || 'Unknown Category'})
                    </p>
                    <p className="text-sm">
                      Building: {linkedInspection.buildingId}, Year:{' '}
                      {linkedInspection.inspYear}
                    </p>
                    <button
                      className="underline text-blue-700 hover:text-blue-500 mt-1"
                      onClick={() => {
                        // Switch to the linked inspection as the "current" one
                        setInspectionData(null);
                        setLinkedInspection(null);
                        // Overwrite the existing ID
                        // so we re-fetch with the linked ID
                        // (Equivalent to "View Linked Inspection")
                        // If you want to store numeric ID in state, do it here
                      }}
                    >
                      View Linked Inspection
                    </button>
                  </div>
                </div>
              )}

              {/* Main Info */}
              <DeviceAccordion label="Main Inspection Information">
                <div
                  className={`flex ${
                    isExpanded ? 'flex-row' : 'flex-col md:flex-row'
                  } gap-4`}
                >
                  <div className="w-full">
                    <RenderFields
                      fields={mainLeft}
                      formData={combinedData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={mainRight}
                      formData={combinedData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </DeviceAccordion>

              {/* Other Info */}
              <DeviceAccordion label="Other Information">
                <div
                  className={`flex ${
                    isExpanded ? 'flex-row' : 'flex-col md:flex-row'
                  } gap-4`}
                >
                  <div className="w-full">
                    <RenderFields
                      fields={otherLeft}
                      formData={combinedData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={otherRight}
                      formData={combinedData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      readOnly={!isEditMode}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </DeviceAccordion>

              {/* Device Table */}
              <DeviceListTable
                inspectionId={inspectionId}
                // Optional: pass buildingSite or other props if needed
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
